import EditDialogProvider, {
  useDialogProviderContext,
} from "src/features/DialogProvider";
import { useAlerting, useTranslation } from "src/hooks";
import Content from "./content";
import { useEffect, useState } from "react";
import {
  TableColumnKey,
  TableRow,
} from "src/views/Ledger/LedgerBalanceMonitor/types/Table";
import useValidation from "./hooks/useValidation";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { GetListRes } from "src/views/Ledger/LedgerBalanceMonitor/types/GetListRes";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "src/utils/helper";
import EnumCriteria from "@wallet-manager/node-types/dist/src/postgres/const/Criteria";
import { editLedgerBalanceMonitor } from "src/api/merchant";
import {
  Action,
  AlertType,
  ILedgerBalanceAlertConfigDetail,
} from "src/api/types";

export type EditFields = {
  alertType: AlertType;
  interval?: string;
  balance?: string;
  thresholdLower: string;
  thresholdUpper: string;
};

function EditDialog({
  rowData,
}: {
  rowData: TableRow & { rawData: GetListRes };
}) {
  const initFields = {
    alertType: rowData[TableColumnKey.AlertType] || AlertType.FixedThreshold,
    interval: rowData[TableColumnKey.StepValue] || "",
    balance: rowData[TableColumnKey.Balance] || "",
    thresholdLower: rowData[TableColumnKey.ThresholdLower] || "",
    thresholdUpper: rowData[TableColumnKey.ThresholdUpper] || "",
  };

  const [fields, setFields] = useState<EditFields>(initFields);
  const { translatePrefix, refreshData } = usePageConfigProviderContext();
  const { t } = useTranslation(translatePrefix);
  const { validate } = useValidation(initFields);
  const { alerting } = useAlerting();

  const { isOpen, setIsOpen } = useDialogProviderContext();

  const { chain_id, chain_type } = rowData.rawData;

  useEffect(() => {
    if (!isOpen) {
      setFields(initFields);
    }
  }, [isOpen]);

  const onConfirm = async () => {
    const isValid = validate(fields);

    if (!isValid || chain_type === undefined || chain_id === undefined) {
      return;
    }

    const decimal = findDecimalByChainNameAndAsset(
      String(chain_type),
      chain_id,
      rowData[TableColumnKey.Currency],
    );

    const getDetails = () => {
      const details: ILedgerBalanceAlertConfigDetail[] = [];

      // Emit upsert records for the current target state
      if (fields.alertType === AlertType.FixedThreshold) {
        if (fields.thresholdLower) {
          details.push({
            action: Action.Upsert,
            criteria: EnumCriteria.LessThan,
            thresholdValue: amountToCryptoAmount(
              fields.thresholdLower,
              Number(decimal),
            ),
          });
        }
        if (fields.thresholdUpper) {
          details.push({
            action: Action.Upsert,
            criteria: EnumCriteria.GreaterThan,
            thresholdValue: amountToCryptoAmount(
              fields.thresholdUpper,
              Number(decimal),
            ),
          });
        }
      } else {
        const stepValue = amountToCryptoAmount(
          fields.interval ?? "0",
          Number(decimal),
        );
        const balanceAmount = amountToCryptoAmount(
          fields.balance ?? "0",
          Number(decimal),
        );
        details.push({
          action: Action.Upsert,
          criteria: EnumCriteria.SlidingLessThan,
          stepValue: stepValue,
          balance: balanceAmount,
        });
        details.push({
          action: Action.Upsert,
          criteria: EnumCriteria.SlidingGreaterThan,
          stepValue: stepValue,
          balance: balanceAmount,
        });
      }

      return details;
    };

    const res = await editLedgerBalanceMonitor({
      assetName: rowData[TableColumnKey.Currency],
      clientId: rowData[TableColumnKey.ClientId],
      details: getDetails(),
      chainType: chain_type,
      chainId: chain_id,
    });

    if (!res) {
      return;
    }

    alerting("success", t("edit_ledger_balance_monitor_success"));
    refreshData();
    setIsOpen(false);
  };

  const isLowerThresholdInvalid =
    fields.alertType === AlertType.FixedThreshold &&
    (Number(fields.thresholdLower) < 0 || isNaN(Number(fields.thresholdLower)));
  const isUpperThresholdInvalid =
    fields.alertType === AlertType.FixedThreshold &&
    fields.thresholdUpper !== "" &&
    (Number(fields.thresholdUpper) <= 0 ||
      isNaN(Number(fields.thresholdUpper)));
  const isIntervalInvalid =
    fields.alertType === AlertType.DynamicThreshold &&
    (!fields.interval ||
      Number(fields.interval) <= 0 ||
      isNaN(Number(fields.interval)));

  const isConfirmDisabled =
    (fields.alertType === AlertType.FixedThreshold &&
      !fields.thresholdLower &&
      !fields.thresholdUpper) ||
    isLowerThresholdInvalid ||
    isUpperThresholdInvalid ||
    isIntervalInvalid;

  return (
    <EditDialogProvider.DialogInOne
      title={t("edit_dialog_title")}
      content={
        <Content
          fields={fields}
          setFields={setFields}
          rowData={rowData}
          isLowerThresholdInvalid={isLowerThresholdInvalid}
          isUpperThresholdInvalid={isUpperThresholdInvalid}
        />
      }
      isConfirmDisabled={isConfirmDisabled}
      onConfirm={onConfirm}
      maxWidth="md"
    />
  );
}

export default EditDialog;
